import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as channelsApi from '@/services/api/channels.api';
import {
  fetchChannelStatus,
  syncChannel,
  syncConnection,
  pushRate,
  fetchSyncLog,
  fetchSyncSchedules,
  createSyncSchedule,
  updateSyncSchedule,
  deleteSyncSchedule,
  triggerSyncSchedule,
  type PushRatePayload,
  type ChannelStatusQuery,
  type SyncLogQuery,
  type CreateSyncSchedulePayload,
  type UpdateSyncSchedulePayload,
} from '@/services/api/channels.api';

export function useChannelStatus(query?: ChannelStatusQuery) {
  return useQuery({
    queryKey: ['channels', 'status', query],
    queryFn: () => fetchChannelStatus(query),
    staleTime: 30_000,
  });
}

export function useSyncLog(query?: SyncLogQuery) {
  return useQuery({
    queryKey: ['channels', 'sync-log', query],
    queryFn: () => fetchSyncLog(query),
    staleTime: 30_000,
  });
}

export function useSyncChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { unitId: string; channel?: string }) =>
      syncChannel(params.unitId, params.channel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

/** Sync EVERY mapped room on a connection (staff "Sync Now"). */
export function useSyncConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) => syncConnection(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

export function usePushRate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PushRatePayload) => pushRate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

export function usePushToChannels() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { unitId: string; channel?: string }) =>
      channelsApi.pushToChannels(vars.unitId, vars.channel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

export function useRetrySyncLog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => channelsApi.retrySyncLog(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

// ---- Connection CRUD hooks ----

export function useCreateConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof channelsApi.createConnection>[0]) =>
      channelsApi.createConnection(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

/** Non-secret connection detail for the edit form (fetched on demand). */
export function useConnectionDetail(id: string | null) {
  return useQuery({
    queryKey: ['channels', 'connection', id],
    queryFn: () => channelsApi.getConnection(id as string),
    enabled: !!id,
    staleTime: 0,
  });
}

export function useUpdateConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof channelsApi.updateConnection>[1] }) =>
      channelsApi.updateConnection(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

export function useDeleteConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => channelsApi.deleteConnection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

export function useTestConnection() {
  return useMutation({
    mutationFn: (id: string) => channelsApi.testConnection(id),
  });
}

export function useUnitMappings(connectionId: string) {
  return useQuery({
    queryKey: ['channels', 'mappings', connectionId],
    queryFn: () => channelsApi.fetchUnitMappings(connectionId),
    enabled: !!connectionId,
  });
}

export function useCreateUnitMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      connectionId,
      data,
    }: {
      connectionId: string;
      data: Parameters<typeof channelsApi.createUnitMapping>[1];
    }) => channelsApi.createUnitMapping(connectionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels', 'mappings'] });
    },
  });
}

export function useDeleteUnitMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ connectionId, mappingId }: { connectionId: string; mappingId: string }) =>
      channelsApi.deleteUnitMapping(connectionId, mappingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels', 'mappings'] });
    },
  });
}

// ─── Sync Schedules ─────────────────────────────────────────────────────────

export function useSyncSchedules() {
  return useQuery({
    queryKey: ['channels', 'schedules'],
    queryFn: () => fetchSyncSchedules(),
    staleTime: 30_000,
  });
}

export function useCreateSyncSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSyncSchedulePayload) =>
      createSyncSchedule(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels', 'schedules'] });
    },
  });
}

export function useUpdateSyncSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateSyncSchedulePayload;
    }) => updateSyncSchedule(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels', 'schedules'] });
    },
  });
}

export function useDeleteSyncSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSyncSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels', 'schedules'] });
    },
  });
}

export function useTriggerSyncSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => triggerSyncSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels', 'schedules'] });
    },
  });
}
